import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ConfigTypes, CTADataTypes, HashTypes } from '../types';
import { CA_MARKET, CAMPAIGN_ID, US_MARKET } from '../constants';
import getMarket from '../functions/getMarket';
import { setModalIsOpen } from '../redux/localConfigReducer';
import useSourceOfLead from './useSourceOfLead';
import { IS_T1_PAGE } from '../constants/environment';

const usePrepareConfig = (config: ConfigTypes) => {
  const [dataFromCta, setDataFromCta] = useState<CTADataTypes | null>(null);
  const [hashData, setHashdata] = useState<HashTypes | null>(null);

  const { generalSource } = useSourceOfLead();

  const dispatch = useDispatch();

  // --------------------------------------------------------------------------------------------------- //
  // Check if we have data in CTA
  // --------------------------------------------------------------------------------------------------- //

  useEffect(() => {
    // condition if desktop mode - just return nothing
    const handleButtonClick = (event: any) => {
      const clickedButton = event.target;
      const ctaInnerText = clickedButton.innerText;
      const clickedCtaData = JSON.parse(clickedButton.getAttribute('data-info'));
      const triggerCta =
        clickedButton.id === 'digital-leads' || clickedButton.id.includes('digital-leads');
      // Set data from CTA
      if (clickedCtaData) {
        setDataFromCta({ ...clickedCtaData, ctaInnerText });
      }

      // Trigger to open Modal
      if (triggerCta) {
        dispatch(setModalIsOpen());
      }

      // check if we have presetForm and save it
      if (triggerCta && clickedButton.id.includes('_') && !!clickedCtaData) {
        const clickedCtaId = JSON.parse(clickedButton.getAttribute('id'));
        const presetFormName = clickedCtaId[1];
        setDataFromCta({ presetForm: presetFormName, ctaInnerText });
      }

      if (clickedButton?.hash?.includes('#contact')) {
        setDataFromCta((prevState) => {
          return { ...prevState, ctaInnerText };
        });
      }
    };
    document.addEventListener('click', handleButtonClick);
    return () => {
      document.removeEventListener('click', handleButtonClick);
    };
  }, [dispatch]);

  // --------------------------------------------------------------------------------------------------- //
  // Check if we have hash in the URL
  // --------------------------------------------------------------------------------------------------- //

  useEffect(() => {
    const handleHashChange = () => {
      const hashTrigger = window.location.hash;

      // Trigger to open Modal
      if (hashTrigger.includes('#contact')) {
        dispatch(setModalIsOpen());
      }

      // check if we have presetForm and save it
      if (hashTrigger.includes('_')) {
        const presetFormName = hashTrigger.split('_')[1];
        setHashdata({ presetForm: presetFormName });
      }
      if (!hashTrigger.includes('_')) {
        const params = new URLSearchParams(window.location.hash.substring(1));
        const formNameFromHash = params.get('form') ?? '';
        const carlineFromHash = params.get('carline') ?? '';
        const audiCodeFromHash = params.get('audiCode') ?? '';
        setHashdata({
          presetForm: formNameFromHash,
          carline: carlineFromHash,
          audiCode: audiCodeFromHash,
        });
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [dispatch]);

  // --------------------------------------------------------------------------------------------------- //
  // MARKET
  // --------------------------------------------------------------------------------------------------- //

  const market = useMemo(() => {
    // Check if we have market data from CTA
    if (dataFromCta?.market)
      return dataFromCta?.market?.toLocaleLowerCase().includes('ca') ? CA_MARKET : US_MARKET;

    const queryParamVehicleId = new URLSearchParams(window.location.search).get('market');

    // Check if we have market data from Query Params
    if (queryParamVehicleId) {
      const transformedMarket = queryParamVehicleId.toLocaleLowerCase().includes('ca')
        ? CA_MARKET
        : US_MARKET;
      return transformedMarket;
    }

    // Check if we have market dat from Config File
    if (config?.market) return config?.market;

    return getMarket();
  }, [config?.market, dataFromCta]);

  // --------------------------------------------------------------------------------------------------- //
  // DealerId
  // --------------------------------------------------------------------------------------------------- //

  const dealerId = useMemo(() => {
    // Check if we have dealer data on page for T3
    const digitalDealerId = window?.digitalData?.dealer
      ? (window?.digitalData?.dealer as any)[0]?.dealerInfo?.dealerID
      : undefined;
    if (digitalDealerId) return digitalDealerId;

    // Check if we have config file
    if (config?.dealerId) return config?.dealerId;

    // Check if we can get dealer data from CTA
    if (dataFromCta?.dealerId) return dataFromCta?.dealerId;

    // Check if we have query param
    const queryParamDealerId = new URLSearchParams(window.location.search).get('dealerId');
    if (queryParamDealerId) return queryParamDealerId;

    return null;
  }, [config?.dealerId, dataFromCta?.dealerId]);

  // --------------------------------------------------------------------------------------------------- //
  // Vehicle Id
  // --------------------------------------------------------------------------------------------------- //

  const vehicleId = useMemo(() => {
    // Check if we have vehicleId in CTA
    if (dataFromCta !== null && dataFromCta?.vehicleId) {
      return dataFromCta?.vehicleId;
    }

    // Check if we have vehicleId in Query params
    const queryParamVehicleId = new URLSearchParams(window.location.search).get('vehicleId');
    if (queryParamVehicleId) {
      return queryParamVehicleId;
    }

    // Check if we have vehicleId in Config
    if (config?.vehicleId) {
      return config?.vehicleId;
    }

    return '';
  }, [config?.vehicleId, dataFromCta]);

  // --------------------------------------------------------------------------------------------------- //
  // Vehicle Type
  // --------------------------------------------------------------------------------------------------- //

  const vehicleType = useMemo(() => {
    const queryParamMarket = new URLSearchParams(window.location.search).get('market');
    if (queryParamMarket) return queryParamMarket.includes('uc') ? 'used' : 'new';
    if (dataFromCta?.vehicleType) return dataFromCta?.vehicleType;
    return 'new';
  }, [dataFromCta?.vehicleType]);

  // --------------------------------------------------------------------------------------------------- //
  // Preset Form Type
  // --------------------------------------------------------------------------------------------------- //

  const presetForm = useMemo(() => {
    // Check if we have form name in hash
    if (hashData !== null && hashData?.presetForm) {
      return hashData?.presetForm;
    }
    // Check if we have preset Form in CTA
    if (dataFromCta !== null && dataFromCta?.presetForm) {
      return dataFromCta?.presetForm;
    }
    const isCustomFormAvailable = !!config?.customForm?.length;
    // Check if we have preset Form in Config
    if (!isCustomFormAvailable && config?.presetForm) {
      return config?.presetForm;
    }

    return 'general';
  }, [config?.customForm?.length, config?.presetForm, dataFromCta, hashData]);

  // --------------------------------------------------------------------------------------------------- //
  // Custom Form Type
  // --------------------------------------------------------------------------------------------------- //

  const customForm = useMemo(() => {
    const isCustomFormAvailable = !!config?.customForm?.length;
    // Check if we have custom Form in Config
    if (!config?.presetForm && isCustomFormAvailable) {
      return config?.customForm;
    }
    return [];
  }, [config?.customForm, config?.presetForm]);

  // --------------------------------------------------------------------------------------------------- //
  // Adobe Cloud Id
  // --------------------------------------------------------------------------------------------------- //

  const adobeCloudId = useMemo(() => {
    const queryParamAdobeCloudId = new URLSearchParams(window.location.search).get('adobeCloudId');
    if (queryParamAdobeCloudId) return queryParamAdobeCloudId;
    if (dataFromCta?.adobeCloudId) {
      return dataFromCta.adobeCloudId;
    }
    // const mcvid = IS_T1_PAGE ? window?.ss?.visitor?.getMarketingCloudVisitorID() : undefined;
    // const mcvidTier3 = !IS_T1_PAGE ? window?.adobeDataLayer?.[0].userData?.userECID : undefined;
    // if (mcvid) return mcvid;
    // if (mcvidTier3) return mcvidTier3;
    return '';
  }, [dataFromCta?.adobeCloudId]);

  // --------------------------------------------------------------------------------------------------- //
  // Campaign Id
  // --------------------------------------------------------------------------------------------------- //

  const campaignId = useMemo(() => {
    const queryParamCampaignId = new URLSearchParams(window.location.search).get('campaignId');
    if (queryParamCampaignId) return queryParamCampaignId;
    if (dataFromCta?.campaignId) {
      return dataFromCta?.campaignId;
    }
    if (market === CA_MARKET && generalSource === 'tier_3') {
      return presetForm === 'general'
        ? CAMPAIGN_ID.vinSpecificContactDealer
        : CAMPAIGN_ID.genericContactDealer;
    }
    return '';
  }, [dataFromCta?.campaignId, generalSource, market, presetForm]);

  // --------------------------------------------------------------------------------------------------- //
  // AudiCode and Carline (for NGC and MLP)
  // --------------------------------------------------------------------------------------------------- //

  const audiCode = hashData?.audiCode ?? null;
  const carline = hashData?.carline ?? null;

  // --------------------------------------------------------------------------------------------------- //
  // Configuration for Success modal
  // --------------------------------------------------------------------------------------------------- //

  const successCustomHeading = config?.headingCustomText;
  const successCustomBody = config?.bodyCustomText;
  const successCustomCtaVerbiage = config?.ctaVerbiageCustom;
  const successCustomCtaUrl = config?.ctaUrlCustom;

  const successModalConfig = {
    successCustomHeading,
    successCustomBody,
    successCustomCtaVerbiage,
    successCustomCtaUrl,
  };

  // --------------------------------------------------------------------------------------------------- //
  // Configuration for Topic Of Interest (general form)
  // --------------------------------------------------------------------------------------------------- //

  const topicOfInterests = config?.topicOfInterestList;

  // Offers data
  const isOfferPage = !!dataFromCta?.offers;
  const offers = dataFromCta?.offers ?? null;

  // configurable legal disclaimer
  const tcpaConsent = config?.tcpaConsent;
  const dealerConsent = config?.dealerConsent;
  const dealerConsentFr = config?.dealerConsentFr;
  const ccpaDisclaimer = config?.ccpaDisclaimer;
  const ccpaConsent = config?.ccpaConsent;

  // Custom department name and Phone
  const customDepartmentName = config?.departmentName ?? null;
  const customPhoneNumber = config?.phoneNumberCMS?.toString() ?? null;

  // Will be inmplemented in the future, for MLP and others
  const modelSalesCode = config?.modelSalesCode;
  const ctaInnerText = dataFromCta?.ctaInnerText ? dataFromCta?.ctaInnerText : null;

  return {
    market,
    dealerId,
    vehicleId,
    vehicleType,
    modelSalesCode,
    presetForm,
    customForm,
    adobeCloudId,
    audiCode,
    carline,
    ctaInnerText,
    campaignId,
    isOfferPage,
    offers,
    tcpaConsent,
    dealerConsent,
    dealerConsentFr,
    ccpaDisclaimer,
    ccpaConsent,
    customDepartmentName,
    customPhoneNumber,
    successModalConfig,
    topicOfInterests,
  };
};

export default usePrepareConfig;
